import { v4 as uuidv4 } from 'uuid';

// Data Layer
import {
  AUTOPX_DISCOUNT_ID,
  ELECTRONIC_DISCOUNT_ID,
} from 'data-layer-cr/dist/constants/discounts';
import {
  defaultUnit,
  getDiscountedTotal,
} from 'data-layer-cr/dist/functions/quotations';
import { makeEmptyDiscount } from 'data-layer-cr/dist/models/factory';

// Store
import getMaskedMimeType from 'src/functions/getMaskedMimeType';
import prepareBase64 from 'src/functions/prepareBase64';
import { RootState } from 'src/store/rootReducer';

// System under test
import YappyEmissionRequest, {
  PersistedEmissionData,
  YappyData,
} from '../../YappyEmissionRequest';
import { makeEmissionRequest } from './makeEmissionRequest';

export const makeYappyData = (states: RootState): YappyData => {
  const { quotation } = states.quotation;
  const {
    autoPxPaymentTotal,
    electronicPaymentTotal,
    paymentTotal,
    paymentFields,
    dataFields,
  } = states.emissionPolicy;
  const { type, paymentQuantityType } = paymentFields;
  const { email, names, lastNames, phoneNumber } = dataFields;

  // build total from source quotation
  const { grandTotal } = getDiscountedTotal({
    /** Use recorded totals in payment step */
    total: {
      ...defaultUnit(quotation).total,
      grandTotal: paymentTotal,
      discounts: [
        {
          ...makeEmptyDiscount(),
          id: ELECTRONIC_DISCOUNT_ID,
          total: electronicPaymentTotal,
        },
        {
          ...makeEmptyDiscount(),
          id: AUTOPX_DISCOUNT_ID,
          total: autoPxPaymentTotal,
        },
      ],
    },
    quotation,
    paymentType: type,
    paymentQuantityType,
  });

  return {
    departmentId: 'MM',
    referenceId: '',
    description: email,
    fullName: `${names} ${lastNames}`,
    total: grandTotal,
    subtotal: parseFloat((grandTotal - 0.01).toFixed(2)),
    discount: 0,
    taxes: 0.01,
    shipping: 0,
    tel: phoneNumber,
    successUrl: `${window.location.origin}/yappy_payment/success`,
    failUrl: `${window.location.origin}/yappy_payment/failure`,
  };
};

export const makePersistedData = (states: RootState): PersistedEmissionData => {
  const {
    emissionPolicy,
    profile,
    quotation,
    creditorCatalog,
    person,
    inspectionForm,
    coverageCatalog,
    stepper,
    carPolicies,
    legalEntityCatalog,
  } = states;

  return {
    emissionPolicy,
    profile,
    quotation,
    creditorCatalog,
    person,
    inspectionForm,
    coverageCatalog,
    stepper,
    carPolicies,
    legalEntityCatalog,
  };
};

export const makeYappyPaymentPayload = (
  states: RootState,
): YappyEmissionRequest => {
  // Properties for the registry
  const { inspectionRequired } = states.coverageCatalog.productConfig;
  const { vehicleRegistryImage } = states.inspectionForm;

  // Request creation
  const { creditCardData, achData, claveData } = states;
  const { quotation } = states.quotation;
  const { id, urlSalt } = quotation;
  const { banks, creditCardBanks } = states.creditorCatalog;
  const { textValues } = states.inspectionForm;
  const { Email: email } = states.profile.profile;
  const userName = states.profile.profile.Name;
  const userId = states.profile.profile.IdUser;
  const userLicense = states.profile.currentLicense;
  const userRole = states.profile.currentRole;

  const formState = states.emissionPolicy;
  const uuid = uuidv4();
  const request = makeEmissionRequest({
    quotation,
    formState,
    bankList: banks,
    inspectionTextValues: textValues,
    uniqueRequestId: uuid,
    creditCardData,
    achData,
    sessionEmails: [email],
    sessionUserName: userName,
    sessionUserId: userId,
    sessionLicense: userLicense,
    sessionRole: userRole,
    creditCardBankList: creditCardBanks,
    claveData,
    cities: states.legalEntityCatalog.cities,
    townships: states.legalEntityCatalog.townships,
  });

  if (!inspectionRequired && vehicleRegistryImage !== '') {
    const mime = getMaskedMimeType(vehicleRegistryImage);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    request.aditionalDocuments.push({
      id: '',
      name: `vehicle-registry-image${extension}`,
      content: prepareBase64(vehicleRegistryImage),
      mime,
    });
  }

  return {
    yappyData: makeYappyData(states),
    emissionData: {
      createdBy: 'SALES',
      quotationId: id.toString(),
      orderId: null,
      urlSalt,
      emissionRequestId: uuid,
      salesEmissionRedux: makePersistedData(states),
      salesEmissionRequest: request,
    },
  };
};
